import { useEffect, useRef } from 'react';
import { useIsForegroundNow } from 'shared/src/hooks';
import type { MarketDetail } from 'shared/src/api';
import { useMumulLiveProfileStatus } from '../queries/useMumulLiveProfileStatus';
import { useMumulLiveCurrentCampaign } from '../queries/useMumulLiveCurrentCampaign';
import { useOnPressMumulLiveProfile } from './useOnPressMumulLiveProfile';
import { useLogLiveProfileBadge } from './useLogLiveProfileBadge';
import { useIsMumulLiveExposed } from './useIsMumulLiveExposed';
import {
  getMumulLiveStatusDisplay,
  type MumulLiveStatusDisplay,
} from '../constants/mumulLiveStatusDisplay';

type UseMarketMumulLiveResult = {
  /** 상태별 프로필 표시 정보. null 이면 노출 없음(DEFAULT/미노출). */
  statusDisplay: MumulLiveStatusDisplay | null;
  /** UNREAD 스토리 여부. 프로필 링 진입 애니메이션 재생 조건. */
  isUnread: boolean;
  onPressProfile: () => void;
};

/**
 * 마켓홈 무물라이브 프로필 관련 로직 일체(EXP 노출 게이트 / 상태·캠페인 조회 /
 * 노출 로깅 / 클릭 액션)를 캡슐화한다.
 */
export const useMarketMumulLive = (
  market: MarketDetail['market'] | undefined,
): UseMarketMumulLiveResult => {
  const profileSno = market?.mumul_live_profile_sno ?? null;
  const profileName = market?.name ?? '';

  // 노출 대상(EXP1788 B군) 여부. refetch 는 enabled 를 무시하고 강제 호출되므로
  // 비노출(대조군)에서 캐시가 채워지지 않도록 refetch 자체를 게이팅한다.
  const isExposed = useIsMumulLiveExposed();

  const {
    data: mumulLiveProfileStatus,
    refetch: refetchMumulLiveProfileStatus,
  } = useMumulLiveProfileStatus(profileSno);

  // 스토리 등 네이티브 화면 갔다가 돌아올 때(포그라운드 복귀)마다 상태 갱신.
  // 마켓홈은 unmount 되지 않아 staleTime/refetchOnMount 로는 갱신되지 않는다.
  const isForeground = useIsForegroundNow();
  const wasForeground = useRef(isForeground);
  useEffect(() => {
    if (isExposed && isForeground && !wasForeground.current) {
      refetchMumulLiveProfileStatus();
    }
    wasForeground.current = isForeground;
  }, [isExposed, isForeground, refetchMumulLiveProfileStatus]);

  const status = mumulLiveProfileStatus?.profile_status;
  const isOnair = status === 'LIVE';

  // LIVE 노출 시 현재 캠페인 조회 (campaign_sno 로깅/랜딩용)
  const { data: currentCampaign, refetch: refetchCurrentCampaign } =
    useMumulLiveCurrentCampaign(profileSno, isOnair);
  const onairCampaignSno = isOnair
    ? (currentCampaign?.campaign?.sno ?? null)
    : null;

  const onPressProfile = useOnPressMumulLiveProfile({
    profileSno,
    profileName,
    status,
    refetchCurrentCampaign,
    refetchStatus: refetchMumulLiveProfileStatus,
  });

  const { logDisplayLiveProfileBadge } = useLogLiveProfileBadge();

  // DISPLAY_LIVE_PROFILE_BADGE: 프로필 뱃지(LIVE/스토리) 노출 시
  // LIVE 는 campaign_sno 가 확보된 뒤 발화, 스토리는 즉시 발화(campaignSno=null)
  useEffect(() => {
    if (profileSno == null || status == null || status === 'DEFAULT') return;
    if (status === 'LIVE' && onairCampaignSno == null) return;

    logDisplayLiveProfileBadge({
      profileSno,
      profileName,
      status,
      campaignSno: onairCampaignSno,
    });
  }, [
    profileSno,
    profileName,
    status,
    onairCampaignSno,
    logDisplayLiveProfileBadge,
  ]);

  return {
    statusDisplay: getMumulLiveStatusDisplay(status),
    isUnread: status === 'HAS_UNREAD_STORY',
    onPressProfile,
  };
};
